
export const BLOCK_WIDTH = 200;
export const COLUMN_PITCH = 360;
export const ROW_GAP = 60;
export const BAND_GAP = 140;
export const MAX_COLUMNS = 5;
export const GROUP_GAP_X = 480; // Category box margins (left 120 + right 280) plus room
export const GROUP_GAP_Y = 260;
export const SHELF_WIDTH = 3300;

const HEADER_HEIGHT = 120;

// Rendered height estimate, see BlockStatement.js (response buttons start at 80px, one every 40px)
export const estimateBlockHeight = (step) => {
    switch (step.type) {
        case 'ask':
            return Array.isArray(step.choices) && step.choices.length > 0 ? 80 + step.choices.length * 40 + 40 : 160;
        case 'say':
        case 'announcement':
            return step.next ? 160 : 120;
        case 'condition':
            return 200;
        case 'categorization':
            return 80 + ((step.branches || []).length + 1) * 40 + 40;
        case 'jump':
            return 80;
        default:
            return 160;
    }
};

const successorsOf = (step) => {
    switch (step.type) {
        case 'ask':
            return Array.isArray(step.choices) && step.choices.length > 0 ? step.choices.map((choice) => choice.next) : [step.next];
        case 'condition':
            return [step.next_true, step.next_false];
        case 'categorization':
            return [...(step.branches || []).map((branch) => branch.next), step.default_next];
        case 'jump':
            return [];
        default:
            return [step.next];
    }
};

const layoutGroup = (nodes) => {
    const byId = new Map(nodes.map((node) => [node.id, node]));
    const order = new Map(nodes.map((node, index) => [node.id, index]));
    const edges = new Map(nodes.map((node) => [node.id, [...new Set(node.successors.filter((id) => id && byId.has(id) && id !== node.id))]]));

    const state = new Map();
    const postorder = [];
    const backEdges = new Set();

    const visit = (id) => {
        state.set(id, 1);
        for (const next of edges.get(id)) {
            if (state.get(next) === 1) {
                backEdges.add(id + '>' + next);
            } else if (!state.has(next)) {
                visit(next);
            }
        }
        state.set(id, 2);
        postorder.push(id);
    };

    for (const node of nodes) {
        if (!state.has(node.id)) visit(node.id);
    }

    const topological = [...postorder].reverse();
    const layer = new Map(nodes.map((node) => [node.id, 0]));
    const parents = new Map(nodes.map((node) => [node.id, []]));

    for (const id of topological) {
        for (const next of edges.get(id)) {
            if (backEdges.has(id + '>' + next)) continue;
            parents.get(next).push(id);
            layer.set(next, Math.max(layer.get(next), layer.get(id) + 1));
        }
    }

    const layerCount = Math.max(...layer.values()) + 1;
    const layers = Array.from({ length: layerCount }, () => []);
    for (const node of nodes) layers[layer.get(node.id)].push(node.id);

    const positions = {};
    let bandTop = 0;
    let bandBottom = 0;
    let width = BLOCK_WIDTH;

    for (let index = 0; index < layerCount; index++) {
        const column = index % MAX_COLUMNS;
        if (column === 0 && index > 0) {
            bandTop = bandBottom + BAND_GAP;
        }

        const desiredY = (id) => {
            const sameBand = parents.get(id).filter((parent) => Math.floor(layer.get(parent) / MAX_COLUMNS) === Math.floor(index / MAX_COLUMNS));
            return sameBand.length > 0 ? Math.min(...sameBand.map((parent) => positions[parent].y)) : bandTop;
        };

        const members = [...layers[index]].sort((a, b) => desiredY(a) - desiredY(b) || order.get(a) - order.get(b));
        let cursor = bandTop;

        for (const id of members) {
            const y = Math.max(desiredY(id), cursor);
            positions[id] = { x: column * COLUMN_PITCH, y };
            cursor = y + byId.get(id).height + ROW_GAP;
            bandBottom = Math.max(bandBottom, y + byId.get(id).height);
        }
        width = Math.max(width, column * COLUMN_PITCH + BLOCK_WIDTH);
    }

    return { positions, width, height: bandBottom };
};

export const computeLayout = (steps, groups = []) => {
    const hasGroups = groups.length > 0;
    const knownGroups = new Set(groups.map((group) => group.id));
    const orphans = hasGroups ? steps.filter((step) => !knownGroups.has(step.group)) : [];
    const groupList = hasGroups ? [...groups, ...(orphans.length > 0 ? [{ id: '__orphans__', name: '', color: null, orphan: true }] : [])] : [{ id: '__all__', name: '', color: null }];

    const placed = groupList.map((group) => {
        const groupSteps = steps.filter((step) => (!hasGroups ? true : group.orphan ? !knownGroups.has(step.group) : step.group === group.id));
        const nodes = groupSteps.map((step) => ({ id: step.id, height: estimateBlockHeight(step), successors: successorsOf(step) }));

        if (hasGroups && !group.orphan) {
            nodes.unshift({ id: '__header__', height: HEADER_HEIGHT, successors: nodes.length > 0 ? [nodes[0].id] : [] });
        }

        return { group, ...layoutGroup(nodes) };
    });

    const positions = {};
    const headers = {};
    const boxes = [];
    let cursorX = 0;
    let cursorY = 0;
    let shelfHeight = 0;

    for (const { group, positions: local, width, height } of placed) {
        if (cursorX > 0 && cursorX + width > SHELF_WIDTH) {
            cursorY += shelfHeight + GROUP_GAP_Y;
            cursorX = 0;
            shelfHeight = 0;
        }

        for (const [id, position] of Object.entries(local)) {
            const absolute = { x: cursorX + position.x, y: cursorY + position.y };
            if (id === '__header__') headers[group.id] = absolute;
            else positions[id] = absolute;
        }

        boxes.push({ id: group.id, x: cursorX, y: cursorY, width, height });
        cursorX += width + GROUP_GAP_X;
        shelfHeight = Math.max(shelfHeight, height);
    }

    return { positions, headers, boxes };
};
